import React from "react";
import { Box, CircularProgress, Tooltip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SAndroid, SRefresh, SSmartphone } from "../../../styles/StyledMUI";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { useMobileDevices } from "../../../contexts/api";
import type { MobileDevice } from "../../../api/mobileDevices/MobileDevice.typed";
import MobileDeviceCard from "./display/MobileDeviceCard";
import ApkDownloadModal from "./display/ApkDownloadModal";
import PairingModal from "./PairingModal";

export default function MobileDevicesPage() {
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const { listDevices, deleteDevice } = useMobileDevices();

  const canPair = hasPermission(Permission.CENTRE_DEVICES_PAIR);
  const canDelete = hasPermission(Permission.CENTRE_DEVICES_REVOKE);

  const [devices, setDevices] = React.useState<MobileDevice[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [pairingOpen, setPairingOpen] = React.useState<boolean>(false);
  const [repairDevice, setRepairDevice] = React.useState<MobileDevice | null>(null);
  const [apkOpen, setApkOpen] = React.useState<boolean>(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await listDevices();
      setDevices(result);
    } finally {
      setLoading(false);
    }
  }, [listDevices]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDelete = React.useCallback(
    async (device: MobileDevice) => {
      const ok = await deleteDevice(device);
      if (ok) await refresh();
    },
    [deleteDevice, refresh],
  );

  const handleRepair = React.useCallback(
    async (device: MobileDevice) => {
      const ok = await showConfirmation(
        "Réappairer l'appareil",
        `Le token actuel de « ${device.label} » sera invalidé. Continuer ?`,
      );

      if (!ok) return;

      setRepairDevice(device);
      setPairingOpen(true);
    },
    [showConfirmation],
  );

  const handleNewPairing = React.useCallback(() => {
    setRepairDevice(null);
    setPairingOpen(true);
  }, []);

  const handlePairingClose = React.useCallback(() => {
    setPairingOpen(false);
    setRepairDevice(null);
    refresh();
  }, [refresh]);

  const hasDevices = devices.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "calc(100vh - 120px)",
        p: 2,
        display: "grid",
        gap: 3,
        overflow: "auto",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mt: 1 }}>
          <Tooltip title="Installer l'APK Android">
            <span>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Installer l'APK Android"
                onClick={() => setApkOpen(true)}
              >
                <SAndroid />
              </IconButtonCTA>
            </span>
          </Tooltip>
          <Tooltip title="Rafraîchir la liste">
            <span>
              <IconButtonCTA
                ctaVariant="neutral"
                aria-label="Rafraîchir la liste"
                onClick={refresh}
                disabled={loading}
              >
                <SRefresh />
              </IconButtonCTA>
            </span>
          </Tooltip>
          <Tooltip title={canPair ? "Appairer un appareil" : "Droits d'appairage requis"}>
            <span>
              <IconButtonCTA
                ctaVariant="accent"
                aria-label="Appairer un appareil"
                onClick={handleNewPairing}
                disabled={!canPair}
              >
                <SSmartphone />
              </IconButtonCTA>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
          {loading && !hasDevices ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size="sm" />
            </Box>
          ) : null}

          {!loading && !hasDevices ? (
            <DarkTypography level="body-md" colorVariant="muted">
              Aucun appareil mobile appairé.
            </DarkTypography>
          ) : null}

          {devices.map((device) => (
            <MobileDeviceCard
              key={device.id}
              device={device}
              canPair={canPair}
              canDelete={canDelete}
              onRepair={handleRepair}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      </Box>

      <ApkDownloadModal open={apkOpen} onClose={() => setApkOpen(false)} />
      <PairingModal open={pairingOpen} onClose={handlePairingClose} device={repairDevice} />
    </Box>
  );
}
